import { computed, Injectable, Signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { map, shareReplay } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActionStatus, LoadScheduleControlFlags, ScreenControlPermission, VISIBILITY_CONTROLS } from '../interfaces/securityPermissions';

export interface RolePermission {
  roleName: string;
  screenName: string;
  actionName: string;
  status: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class RolePermissionsService {
  private permissionsSubject = new BehaviorSubject<RolePermission[] | null>(null);
  private securityPermissionsSubject  = new BehaviorSubject<LoadScheduleControlFlags | null>(null);
  private allPermissionsSignal = toSignal(this.permissionsSubject.asObservable());
  screenPermissions: ActionStatus = new ActionStatus();
  baseUrl = environment.baseUrl;
  private apiUrl = this.baseUrl;
  private rolePermissions$?: Observable<RolePermission[]>;

  constructor(private http: HttpClient) { }

  getRolePermissions(roleName: string): Observable<RolePermission[]> {
    this.apiUrl = this.baseUrl + 'user/GetSecurityActionsByRole?rolename=' + roleName;
    if (this.permissionsSubject.value) {
      return of(this.permissionsSubject.value);
    }
    
    return this.http.get<RolePermission[]>(this.apiUrl).pipe(
      tap(data => this.permissionsSubject.next(data))
    );
  }
  
  getRolePermissionsNew(roleName: string, screen: string, loadId: number | null): Observable<LoadScheduleControlFlags> {
    this.apiUrl = this.baseUrl + 'user/GetSecurityScreenControlsAccessByRoles?RolesNames=' + roleName + '&ScreenName=' + screen + '&FSLoadScheduleId=' + loadId;
    
    return this.http.get<ScreenControlPermission[]>(this.apiUrl).pipe(
      map(rows => this.buildFlagsFromApiResponse(rows)),
      tap(flags => this.securityPermissionsSubject.next(flags))
    );
  }

  buildFlagsFromApiResponse(rows: ScreenControlPermission[]): LoadScheduleControlFlags {
    const flags = new LoadScheduleControlFlags();
    rows.forEach(row => {
      const ctrl = row.control;
      if (VISIBILITY_CONTROLS.has(ctrl)) {
        switch (ctrl) {
          case 'btnSave': flags.saveButton = row.isWriteAccess === true; break;
          case 'addButton': flags.addButton = row.isWriteAccess === true; break;
          case 'addionalInfo': flags.addionalInfo = row.isWriteAccess === true; break
          case 'delete': flags.delete = row.isWriteAccess === true; break
          case 'dispatch_Button': flags.dispatch_Button = row.isWriteAccess === true; break
          case 'dispatchInfo': flags.dispatchInfo = row.isWriteAccess === true; break
          case 'duplicate': flags.duplicate = row.isWriteAccess === true; break
          case 'edit': flags.edit = row.isWriteAccess === true; break
          case 'sampleStatusChange': flags.sampleStatusChange = row.isWriteAccess === true; break
          case 'addStreamButton':  flags.addStreamButton = row.isWriteAccess === true; break
          case 'editStreamButton':  flags.editStreamButton = row.isWriteAccess === true; break
          case 'deleteStreamButton':  flags.deleteStreamButton = row.isWriteAccess === true; break
          case 'showDocumentDeleteButton': flags.showDocumentDeleteButton = row.isWriteAccess === true; break
          case 'updateStatus': flags.updateStatus = row.isWriteAccess === true; break;
          case 'addCustomer': flags.addCustomer = row.isWriteAccess === true; break;
          case 'editCustomer': flags.editCustomer = row.isWriteAccess === true; break;
          case 'deleteCustomer': flags.deleteCustomer = row.isWriteAccess === true; break;
          case 'addStream': flags.addStream = row.isWriteAccess === true; break;
          case 'editStream': flags.editStream = row.isWriteAccess === true; break;
          case 'addExistingStream': flags.addExistingStream = row.isWriteAccess === true; break;
          case 'deleteStream': flags.deleteStream = row.isWriteAccess === true; break;
          case 'addPriceMatrix': flags.addPriceMatrix = row.isWriteAccess === true; break;
          case 'editPriceMatrix': flags.editPriceMatrix = row.isWriteAccess === true; break;
          case 'duplicatePriceMatrix': flags.duplicatePriceMatrix = row.isWriteAccess === true; break;
          case 'deletePriceMatrix': flags.deletePriceMatrix = row.isWriteAccess === true; break;
          case 'addTotePriceMatrix': flags.addTotePriceMatrix = row.isWriteAccess === true; break;
          case 'editTotePriceMatrix': flags.editTotePriceMatrix = row.isWriteAccess === true; break;
          case 'duplicateTotePriceMatrix': flags.duplicateTotePriceMatrix = row.isWriteAccess === true; break;
          case 'deleteTotePriceMatrix': flags.deleteTotePriceMatrix = row.isWriteAccess === true; break;
          case 'addChemistry': flags.addChemistry = row.isWriteAccess === true; break;
          case 'editChemistry': flags.editChemistry = row.isWriteAccess === true; break;
          case 'deleteChemistry': flags.deleteChemistry = row.isWriteAccess === true; break;
          case 'addMetal': flags.addMetal = row.isWriteAccess === true; break;
          case 'editMetal': flags.editMetal = row.isWriteAccess === true; break;
          case 'deleteMetal': flags.deleteMetal = row.isWriteAccess === true; break;
          case 'showToteTab': flags.showToteTab = row.isWriteAccess === true; break;
          case 'showLabTab': flags.showLabTab = row.isWriteAccess === true; break;
          case 'addSalesRep': flags.addSalesRep = row.isWriteAccess === true; break;
          case 'editSalesRep': flags.editSalesRep = row.isWriteAccess === true; break;
          case 'deleteSalesRep': flags.deleteSalesRep = row.isWriteAccess === true; break;
          case 'allowIsReviewdToggle': flags.allowIsReviewdToggle = row.isWriteAccess === true; break;
          case 'reprocess': flags.reprocess = row.isWriteAccess === true; break;
          case 'generateReport': flags.generateReport = row.isWriteAccess === true; break;
        }
      } else if(ctrl in flags) {
        (flags as any)[ctrl] = !row.isWriteAccess;
      }
    });
    return flags;
  }

  permissions$(): Observable<RolePermission[] | null> {
    return this.permissionsSubject.asObservable();
  }

  clearCache(): void {
    this.rolePermissions$ = undefined;
  }

  getScreenActions(screenName: string): Signal<ActionStatus> {
    return computed(() => {
      const status = new ActionStatus();
      const allPermissions = this.allPermissionsSignal();
      console.log(`Computing permissions for screen: ${screenName}`, allPermissions);
      if (!allPermissions) return status;

      // 1. Filter for the specific screen
      const screenPerms = allPermissions.filter(p => p.screenName === screenName);

      // 2. Map DB ActionNames to Class Properties
      Object.keys(status).forEach((key) => {
        const hasPermission = screenPerms.some(
          (p) => p.actionName.trim() === key && p.status === true
        );

        // Update the property (using 'any' to allow dynamic key assignment)
        (status as any)[key] = hasPermission;
      });

      return status;
    });
  }
}
